import { AlertTriangle } from 'lucide-react'
import type { ActionTypeDefinition } from '@/constants/actions'
import { cn } from '@/lib/utils'

interface ActionTypeCardProps {
  definition: ActionTypeDefinition
  disabled?: boolean
  availableAP: number
  onClick: () => void
}

export function ActionTypeCard({
  definition,
  disabled,
  availableAP,
  onClick,
}: ActionTypeCardProps) {
  const { icon, label, description, apRange, hasWarning, warningText } =
    definition
  const [minAP] = apRange

  // Disable if not enough AP for minimum cost
  const insufficientAP = availableAP < minAP
  const isDisabled = disabled || insufficientAP

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        'flex flex-col items-start rounded-lg border p-4 text-left transition-all',
        'hover:border-primary hover:bg-accent hover:shadow-md',
        'focus:ring-primary focus:outline-none focus:ring-2 focus:ring-offset-2',
        isDisabled && 'cursor-not-allowed opacity-50 hover:border-border hover:bg-transparent hover:shadow-none'
      )}
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl" role="img" aria-label={label}>
            {icon}
          </span>
          <span className="font-semibold uppercase tracking-wide">{label}</span>
        </div>
        {hasWarning && (
          <div
            className="text-amber-500 flex items-center gap-1"
            title={warningText}
          >
            <AlertTriangle className="h-4 w-4" />
          </div>
        )}
      </div>

      <p className="text-muted-foreground mt-2 text-sm">{description}</p>

      <div className="mt-3 flex w-full items-center justify-between">
        <span
          className={cn(
            'text-sm font-medium',
            insufficientAP ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {apRange[0]}-{apRange[1]} AP
        </span>
        {insufficientAP && (
          <span className="text-destructive text-xs">Not enough AP</span>
        )}
      </div>
    </button>
  )
}
